import { z } from "zod";

/*
 * Stufe 0 von Angebote, Aufträge und Rechnungen: gemeinsame Kundenkartei, Firmenangaben für Belege, Leistungsstamm.
 * Geldbeträge sind immer ganze Cent (netto), damit nie Rundungsfehler entstehen.
 */

export const CUSTOMER_KINDS = ["company", "person"] as const;
export type CustomerKind = (typeof CUSTOMER_KINDS)[number];
export const CUSTOMER_ROLES = ["customer", "supplier", "partner", "prospect"] as const;
export type CustomerRole = (typeof CUSTOMER_ROLES)[number];

export const ROLE_LABEL: Record<CustomerRole, string> = {
  customer: "Kunde",
  supplier: "Lieferant",
  partner: "Partner",
  prospect: "Interessent",
};
export const ROLE_PLURAL: Record<CustomerRole, string> = {
  customer: "Kunden",
  supplier: "Lieferanten",
  partner: "Partner",
  prospect: "Interessenten",
};
export const KIND_LABEL: Record<CustomerKind, string> = { company: "Firma", person: "Person" };

export const VAT_RATES = [0, 7, 19] as const;
export const ITEM_KINDS = ["service", "product"] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

export interface CustomerInfo {
  id: string;
  number: number;
  kind: CustomerKind;
  role: CustomerRole;
  name: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  vatId: string | null;
  paymentDays: number | null;
  tags: string[];
  note: string | null;
  archived: boolean;
  projectCount: number;
  createdAt: string;
}

export interface CustomerDetail extends CustomerInfo {
  projects: { id: string; name: string; status: "active" | "archived" }[];
}

/** Projekte, deren Kunden-Textfeld noch keiner Kundenkarte zugeordnet ist (Vorschlag zum Übernehmen). */
export interface CustomerSuggestion {
  name: string;
  projectCount: number;
}

export interface BillingSettings {
  legalName: string | null;
  ownerName: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  taxNumber: string | null;
  vatId: string | null;
  registerInfo: string | null;
  bankName: string | null;
  iban: string | null;
  bic: string | null;
  smallBusiness: boolean;
  paymentDays: number;
  /** Akzentfarbe der PDFs als #rrggbb, null = Standardblau. */
  pdfAccent: string | null;
  /** Überweisungs-QR-Code (Girocode) auf Rechnungen. */
  giroQr: boolean;
  /** Nächste freie Kundennummer; einstellbar, damit Nummern aus einem anderen Programm lückenlos weiterlaufen. */
  nextCustomerNumber: number;
}

export interface ItemInfo {
  id: string;
  kind: ItemKind;
  name: string;
  description: string | null;
  unit: string;
  priceCents: number;
  vatRate: number;
  archived: boolean;
}

// ---------- Prüfungen ----------

/** IBAN nach Prüfziffer (Modulo 97), Leerzeichen sind erlaubt. */
export function isValidIban(raw: string): boolean {
  const iban = raw.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  if (iban.startsWith("DE") && iban.length !== 22) return false;
  const moved = iban.slice(4) + iban.slice(0, 4);
  let rest = 0;
  for (const ch of moved) {
    const digits = /[A-Z]/.test(ch) ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of digits) rest = (rest * 10 + Number(d)) % 97;
  }
  return rest === 1;
}

export function isValidVatId(raw: string): boolean {
  const v = raw.replace(/\s+/g, "").toUpperCase();
  if (v.startsWith("DE")) return /^DE[0-9]{9}$/.test(v);
  return /^[A-Z]{2}[A-Z0-9]{2,12}$/.test(v);
}

/** Deutsche Steuernummer, so wie sie das Finanzamt schreibt (z. B. 123/456/78901 oder 12 345 67890). */
export function isValidTaxNumber(raw: string): boolean {
  const v = raw.trim();
  return /^[0-9][0-9 /]{7,15}[0-9]$/.test(v) && v.replace(/\D/g, "").length >= 9 && v.replace(/\D/g, "").length <= 13;
}

export const compactIban = (raw: string) => raw.replace(/\s+/g, "").toUpperCase();
export const compactVatId = (raw: string) => raw.replace(/\s+/g, "").toUpperCase();

// ---------- Schemas ----------

const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Bitte höchstens ${max} Zeichen.`)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null));

const optEmail = z
  .string()
  .trim()
  .max(254)
  .nullable()
  .optional()
  .transform((v) => (v ? v.toLowerCase() : null))
  .refine((v) => v === null || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v), "Bitte geben Sie eine gültige E-Mail-Adresse an.");

const optVatId = z
  .string()
  .trim()
  .max(20)
  .nullable()
  .optional()
  .transform((v) => (v ? compactVatId(v) : null))
  .refine((v) => v === null || isValidVatId(v), "Diese Umsatzsteuer-ID sieht nicht richtig aus (Beispiel: DE123456789).");

const tags = z
  .array(z.string().trim().min(1).max(30))
  .max(12, "Bitte höchstens 12 Stichworte.")
  .optional()
  .transform((list) => [...new Set((list ?? []).map((t) => t.trim()).filter(Boolean))]);

const customerFields = {
  kind: z.enum(CUSTOMER_KINDS),
  role: z.enum(CUSTOMER_ROLES),
  name: z.string().trim().min(1, "Bitte geben Sie einen Namen an.").max(160, "Bitte höchstens 160 Zeichen."),
  contactName: optText(120),
  email: optEmail,
  phone: optText(40),
  street: optText(120),
  postalCode: optText(12),
  city: optText(80),
  country: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, "Bitte geben Sie das Land als Kürzel an, z. B. DE.")
    .optional(),
  vatId: optVatId,
  paymentDays: z.number().int().min(0).max(365).nullable().optional(),
  tags,
  note: optText(2000),
  /** Nur beim Anlegen oder Importieren: eigene Nummer, z. B. aus einem anderen Programm. */
  number: z.number().int().min(1).max(99_999_999).optional(),
};

export const customerCreateSchema = z.object({
  ...customerFields,
  kind: customerFields.kind.default("company"),
  role: customerFields.role.default("customer"),
});

export const customerUpdateSchema = z
  .object({ ...customerFields, archived: z.boolean() })
  .partial()
  .omit({ number: true });

export const customerImportSchema = z.object({
  customers: z.array(customerCreateSchema).min(1, "Es gibt nichts zu importieren.").max(2000, "Bitte höchstens 2000 Kontakte auf einmal."),
});

export const customerFromProjectsSchema = z.object({
  names: z.array(z.string().trim().min(1).max(120)).min(1, "Bitte wählen Sie mindestens einen Namen.").max(500),
});

const money = z
  .number()
  .int("Der Preis muss in ganzen Cent angegeben sein.")
  .min(0, "Der Preis darf nicht negativ sein.")
  .max(99_999_999_99, "Dieser Preis ist zu hoch.");

export const itemSchema = z.object({
  kind: z.enum(ITEM_KINDS).default("service"),
  name: z.string().trim().min(1, "Bitte geben Sie eine Bezeichnung an.").max(160),
  description: optText(2000),
  unit: z.string().trim().min(1, "Bitte geben Sie eine Einheit an (z. B. Std, Stk, m).").max(12),
  priceCents: money,
  vatRate: z.number().refine((v) => (VAT_RATES as readonly number[]).includes(v), "Der Steuersatz kann 0, 7 oder 19 Prozent sein."),
});
export const itemUpdateSchema = itemSchema.partial().extend({ archived: z.boolean().optional() });

const optIban = z
  .string()
  .trim()
  .max(40)
  .nullable()
  .optional()
  .transform((v) => (v ? compactIban(v) : null))
  .refine((v) => v === null || isValidIban(v), "Diese IBAN stimmt nicht (Prüfziffer falsch). Bitte noch einmal vergleichen.");

export const DEFAULT_PDF_ACCENT = "#1e3863";

export const billingSettingsSchema = z.object({
  giroQr: z.boolean().optional().default(false),
  pdfAccent: z
    .string()
    .trim()
    .toLowerCase()
    .nullable()
    .optional()
    .transform((v) => v || null)
    .refine((v) => v === null || /^#[0-9a-f]{6}$/.test(v), "Bitte wählen Sie die Farbe mit dem Farbfeld aus.")
    .refine((v) => {
      if (v === null) return true;
      const [r, g, b] = [1, 3, 5].map((i) => parseInt(v.slice(i, i + 2), 16));
      // Zu helle Farben sind auf weißem Papier schlecht lesbar.
      return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! < 170;
    }, "Diese Farbe ist für Schrift auf weißem Papier zu hell. Bitte wählen Sie einen dunkleren Ton."),
  legalName: optText(160),
  ownerName: optText(120),
  street: optText(120),
  postalCode: optText(12),
  city: optText(80),
  country: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, "Bitte geben Sie das Land als Kürzel an, z. B. DE.")
    .default("DE"),
  phone: optText(40),
  email: optEmail,
  website: optText(200),
  taxNumber: z
    .string()
    .trim()
    .max(20)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null))
    .refine((v) => v === null || isValidTaxNumber(v), "Diese Steuernummer sieht nicht richtig aus (Beispiel: 123/456/78901)."),
  vatId: optVatId,
  registerInfo: optText(200),
  bankName: optText(80),
  iban: optIban,
  bic: z
    .string()
    .trim()
    .toUpperCase()
    .max(11)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null))
    .refine((v) => v === null || /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(v), "Diese BIC sieht nicht richtig aus (8 oder 11 Zeichen)."),
  smallBusiness: z.boolean().default(false),
  paymentDays: z.number().int().min(0).max(365).default(14),
  nextCustomerNumber: z.number().int().min(1).max(99_999_999).optional(),
});

// ---------- CSV-Import (z. B. Export aus sevDesk oder Excel) ----------

/** Zerlegt CSV-Text (Semikolon oder Komma, Anführungszeichen) in Zeilen. */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, "");
  const first = src.split(/\r?\n/, 1)[0] ?? "";
  const delimiter = (first.match(/;/g)?.length ?? 0) >= (first.match(/,/g)?.length ?? 0) ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(cell);
      cell = "";
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim() !== "")) rows.push(row);
  return rows;
}

type CsvField =
  | "number" | "name" | "company" | "firstName" | "lastName" | "street" | "postalCode" | "city" | "country"
  | "email" | "phone" | "vatId" | "note" | "role";

const HEADER_WORDS: Record<CsvField, string[]> = {
  number: ["kundennummer", "kundennr", "kunden-nr", "knd-nr", "knd.-nr.", "nummer", "kontaktnummer"],
  company: ["organisation", "firma", "firmenname", "unternehmen", "company"],
  name: ["name", "kontaktname", "bezeichnung"],
  firstName: ["vorname", "firstname"],
  lastName: ["nachname", "familienname", "lastname"],
  street: ["straße", "strasse", "straße und hausnummer", "anschrift", "adresse"],
  postalCode: ["plz", "postleitzahl"],
  city: ["ort", "stadt", "city"],
  country: ["land", "country"],
  email: ["e-mail", "email", "mail", "e-mail-adresse"],
  phone: ["telefon", "telefonnummer", "tel", "mobil", "handy", "phone"],
  vatId: ["umsatzsteuer-id", "ust-id", "ustid", "ust-idnr", "umsatzsteuer-identifikationsnummer", "vat"],
  note: ["notiz", "bemerkung", "beschreibung", "kommentar"],
  role: ["kategorie", "typ", "kontaktart", "rolle"],
};

const COUNTRY_CODE: Record<string, string> = {
  deutschland: "DE", germany: "DE", österreich: "AT", austria: "AT", schweiz: "CH", switzerland: "CH",
  polen: "PL", tschechien: "CZ", frankreich: "FR", niederlande: "NL", italien: "IT", spanien: "ES",
};

function roleFrom(raw: string): CustomerRole {
  const v = raw.trim().toLowerCase();
  if (v.startsWith("lieferant")) return "supplier";
  if (v.startsWith("partner")) return "partner";
  if (v.startsWith("interessent")) return "prospect";
  return "customer";
}

export interface CsvCustomer {
  /** Nummer des Eintrags in der Datei (Kopfzeile ist 1, leere Zeilen zählen nicht). */
  line: number;
  customer: z.input<typeof customerCreateSchema>;
}
export interface CsvImportResult {
  customers: CsvCustomer[];
  skipped: { line: number; reason: string }[];
  /** Spalten, die erkannt wurden (zur Anzeige), und solche, die ignoriert werden. */
  recognised: string[];
  ignored: string[];
}

/**
 * Wandelt die Zeilen einer CSV-Datei in Kontakte um. Die Spaltennamen werden großzügig erkannt
 * (Kundennummer, Organisation, Vorname, Nachname, Straße, PLZ, Ort, Land, E-Mail, Telefon, Umsatzsteuer-ID …).
 */
export function customersFromCsv(text: string): CsvImportResult {
  const rows = parseCsv(text);
  const result: CsvImportResult = { customers: [], skipped: [], recognised: [], ignored: [] };
  const header = rows[0];
  if (!header) return result;
  const index: Partial<Record<CsvField, number>> = {};
  header.forEach((h, i) => {
    const key = h.trim().toLowerCase();
    const field = (Object.keys(HEADER_WORDS) as CsvField[]).find((f) => HEADER_WORDS[f].includes(key));
    if (field && index[field] === undefined) {
      index[field] = i;
      result.recognised.push(h.trim());
    } else result.ignored.push(h.trim());
  });
  const cell = (r: string[], f: CsvField) => (index[f] === undefined ? "" : (r[index[f]!] ?? "").trim());
  rows.slice(1).forEach((r, i) => {
    const line = i + 2;
    const company = cell(r, "company");
    const person = [cell(r, "firstName"), cell(r, "lastName")].filter(Boolean).join(" ");
    const name = company || cell(r, "name") || person;
    if (!name) {
      result.skipped.push({ line, reason: "kein Name" });
      return;
    }
    const numberText = cell(r, "number").replace(/\D/g, "");
    const country = cell(r, "country");
    const countryCode = /^[A-Za-z]{2}$/.test(country) ? country.toUpperCase() : COUNTRY_CODE[country.toLowerCase()];
    const vat = cell(r, "vatId");
    result.customers.push({
      line,
      customer: {
        number: numberText ? Number(numberText) : undefined,
        kind: company ? "company" : "person",
        role: roleFrom(cell(r, "role")),
        name,
        contactName: company && person ? person : null,
        email: cell(r, "email") || null,
        phone: cell(r, "phone") || null,
        street: cell(r, "street") || null,
        postalCode: cell(r, "postalCode") || null,
        city: cell(r, "city") || null,
        country: countryCode,
        vatId: vat && isValidVatId(vat) ? vat : null,
        note: cell(r, "note") || null,
      },
    });
  });
  return result;
}

/** „4007 · Rolf Beispiel“, für Auswahllisten und Überschriften. */
export function customerLabel(c: { number: number; name: string }): string {
  return `${c.number} · ${c.name}`;
}

export function formatCents(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}
